import React, { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./lib/firebase";
import { DataProvider } from "./context/DataContext";
import Header from "./components/layout/Header";
import Dashboard from "./components/layout/Dashboard";
import SignUp from "./components/auth/SignUp";
import Login from "./components/auth/Login";

// Protected Route Component
const ProtectedRoute = ({ children, user }) => {
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AuthRoute = ({ children, user, claims, requireDashboardClaim }) => {
  // No user? Show the auth page (login/signup)
  if (!user) return children;

  // User exists but email not verified? Show the auth page
  // This prevents redirect to dashboard for unverified users
  if (!user.emailVerified) return children;

  // User has dashboard claim requirement but doesn't have it? Show auth page
  if (requireDashboardClaim && claims?.role !== "dashboard") {
    return children;
  }

  // User is authenticated and verified, redirect to dashboard
  return <Navigate to="/" replace />;
};

// Main App Content Component
const AppContent = ({ user, loading, claims }) => {
  const navigate = useNavigate();
  const handleLoginSuccess = (newUser) => {
    // Go to app root under the basename
    navigate("/", { replace: true });
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const handleSwitchToLogin = () => {
    navigate("/login");
  };

  const handleSwitchToSignUp = () => {
    navigate("/signup");
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <AuthRoute user={user} claims={claims}>
            <Login
              onSuccess={handleLoginSuccess}
              onSwitchToSignUp={handleSwitchToSignUp}
            />
          </AuthRoute>
        }
      />

      <Route
        path="/signup"
        element={
          <AuthRoute user={user} claims={claims} requireDashboardClaim>
            <SignUp onSwitchToLogin={handleSwitchToLogin} />
          </AuthRoute>
        }
      />

      {/* Protected Routes */}
      {/* App root (relative to basename) is the protected Dashboard */}
      <Route
        path="/"
        element={
          <ProtectedRoute user={user}>
            <DataProvider user={user}>
              <div className="App">
                <Header user={user} onSignOut={handleSignOut} />
                <Dashboard />
              </div>
            </DataProvider>
          </ProtectedRoute>
        }
      />

      {/* Catch all route - redirect to appropriate page */}
      <Route
        path="*"
        element={<Navigate to={user ? "/" : "/login"} replace />}
      />
    </Routes>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      setUser(currentUser);

      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        setClaims(idTokenResult.claims);

        // Poll for role=dashboard claim if not present yet
        if (idTokenResult.claims?.role !== "dashboard") {
          let attempts = 0;
          pollIntervalRef.current = setInterval(async () => {
            attempts++;
            await currentUser.getIdToken(true);
            const refreshed = await currentUser.getIdTokenResult();

            if (refreshed.claims?.role === "dashboard") {
              setClaims(refreshed.claims);
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            } else if (attempts >= 8) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }, 1000);
        }
      } else {
        setClaims(null);
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  return <AppContent user={user} loading={loading} claims={claims} />;
}

export default App;
